import { Component, inject, Input, input, OnInit } from '@angular/core';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import {
  DialPlanGroupEntry,
  WebexDialPlanFieldConfig,
  WebexFieldConfig,
  WebexFieldConfigV2,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroFormConfig } from '../../../../forms/ziro-forms-models';
import { ExtensionDetailsEntity } from '../extension-details-form/extension-details-form.component';
import { ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import { WebexCallingDialPlanInventoriesResource } from '../../../shared/resources/webex-calling-dial-plan-inventories.resource';
import {
  ExtensionSelectorSize,
  ZiroExtensionSelectorConfig,
} from '../../../shared/extension-selector/extension-selector.component';
import { UCM_LICENSE_NAME } from '../webex-calling.component';
import { Observable } from 'rxjs';

export interface DidSelectorFormData {
  location: string;
  did: string;
  webexCallingLicense: string;
}

@Component({
  selector: 'ziro-did-selector-form',
  templateUrl: './did-selector-form.component.html',
  styleUrls: ['./did-selector-form.component.scss'],
  standalone: false,
})
export class ZiroDidSelectorFormComponent extends ZiroFormAbstractDirective<DidSelectorFormData> implements OnInit {
  private _webexCallingDialPlanInventoriesResource = inject(WebexCallingDialPlanInventoriesResource);
  protected _ziroFormStateService: ZiroFormStateService;

  UCM_LICENSE_NAME = UCM_LICENSE_NAME;

  readonly dialPlanGroups = input<WebexDialPlanFieldConfig[]>([]);
  readonly webexFieldConfigV1 = input<WebexFieldConfig>();
  readonly webexFieldConfigV2 = input<WebexFieldConfigV2>();
  readonly enableWebexPerSite = input<boolean>();

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set extensionDetailsEntity(newVal: ExtensionDetailsEntity) {
    this._extensionDetailsEntity = newVal;
    this._setFieldStates();
  }

  get extensionDetailsEntity(): ExtensionDetailsEntity {
    return this._extensionDetailsEntity;
  }

  private _extensionDetailsEntity: ExtensionDetailsEntity;

  formConfig: ZiroFormConfig;
  selectedDialPlanGroup: WebexDialPlanFieldConfig;
  selectedDidOptions: DialPlanGroupEntry[] = [];

  didSelectorSize = ExtensionSelectorSize.SMALL;
  ziroIcons = ZiroIcons;
  showRecentlyDeleted = true;
  showUnavailable = false;
  isSubmitted = false;
  isSearching = false;

  ngOnInit(): void {
    if (this.entity && this.entity.location) {
      const match = this.dialPlanGroups().find((dialPlanGroup) => dialPlanGroup.groupName === this.entity.location);
      if (match) {
        this.isSearching = true;
        this.selectedDialPlanGroup = match;
        this._getEntriesFromGroupName();
      }
    }
    this._initFormConfig();
    this._validateAndSubmitSource.subscribe(() => {
      this.isSubmitted = true;
    });
  }

  applyComponentConfig(): void {
    const didComponent = this.fieldComponents.find((field) => field.fieldId() === 'did');
    didComponent.applyComponentConfig(
      new ZiroExtensionSelectorConfig({
        options: this.selectedDidOptions,
        showRecentlyDeleted: this.showRecentlyDeleted,
        showUnavailable: this.showUnavailable,
        size: this.didSelectorSize,
        showInactive: false,
      }),
    );
  }

  onDialPlanGroupChange(): void {
    this.isSearching = true;
    this.selectedDidOptions = [];
    if (this.selectedDialPlanGroup && this.selectedDialPlanGroup.groupId) {
      this._getEntriesFromGroupName();
      this.entitySource.next({
        ...this.entity,
        did: this.entity.location === this.selectedDialPlanGroup.groupName ? this.entity.did : null,
        location: this.selectedDialPlanGroup.groupName,
      });
      this._ziroFormStateService.setIsFormDirty(true);
    } else {
      this.applyComponentConfig();
      this.entitySource.next({ ...this.entity, did: null, location: null });
      this._ziroFormStateService.setIsFormDirty(true);
      this.isSearching = false;
    }
  }

  toEntity = (formData: DidSelectorFormData): DidSelectorFormData => {
    return {
      webexCallingLicense: formData.webexCallingLicense,
      location: this.selectedDialPlanGroup ? this.selectedDialPlanGroup.groupName : null,
      did: formData.did,
    };
  };

  protected submit(): Observable<any> {
    return this.parentSubmit$();
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        webexCallingLicense: {
          label: 'tkey;helpdesk.webex_calling.extension_details.calling_license.label',
          dataAutomation: 'webex-calling-license',
          componentConfig: new ZiroSelectConfig({
            options: this.enableWebexPerSite()
              ? this.webexFieldConfigV2().licenseType.possibleOptions
              : this.webexFieldConfigV1().webexCallingLicenseJson.possibleOptions,
          }),
          required: this.enableWebexPerSite()
            ? this.webexFieldConfigV2().licenseType.required
            : this.webexFieldConfigV1().webexCallingLicenseJson.required,
          defaultValue: () =>
            this.enableWebexPerSite()
              ? this.webexFieldConfigV2().licenseType.defaultValue
              : this.webexFieldConfigV1().webexCallingLicenseJson.defaultValue,
          hidden: () => {
            return this.enableWebexPerSite()
              ? !this.webexFieldConfigV2().licenseType.show &&
                  // in an env with only one license, show is false. we need to override this for UCM to avoid a blank form.
                  this.webexFieldConfigV2().licenseType.defaultValue !== UCM_LICENSE_NAME
              : !this.webexFieldConfigV1().webexCallingLicenseJson.show &&
                  // in an env with only one license, show is false. we need to override this for UCM to avoid a blank form.
                  this.webexFieldConfigV1().webexCallingLicenseJson.defaultValue !== UCM_LICENSE_NAME;
          },
        },
        location: {
          label: 'tkey;helpdesk.webex_calling.location_name.label.text',
          dataAutomation: 'did-extension-selector-location',
          hidden: () => true,
          required: () => true,
          componentConfig: new ZiroSelectConfig({
            options: [],
          }),
          valExcluded: () => this.formData?.webexCallingLicense === UCM_LICENSE_NAME,
        },
        did: {
          label: 'tkey;helpdesk.webex_calling.available_dids.label.text',
          dataAutomation: 'did-extension-selector',
          required: () => !this.extensionDetailsEntity.extension,
          componentConfig: new ZiroExtensionSelectorConfig({
            options: this.selectedDidOptions,
            showRecentlyDeleted: this.showRecentlyDeleted,
            showUnavailable: this.showUnavailable,
            size: this.didSelectorSize,
          }),
          valExcluded: () => this.formData?.webexCallingLicense === UCM_LICENSE_NAME,
        },
      },
      options: {
        columnClasses: {
          label: 'col-12',
          input: 'col-12',
        },
      },
    };
  }

  private _getEntriesFromGroupName(): void {
    this._webexCallingDialPlanInventoriesResource.get(this.selectedDialPlanGroup.groupId).subscribe((dialPlanGroup) => {
      this.selectedDidOptions = dialPlanGroup.dialPlanEntries;
      this.applyComponentConfig();
      this.isSearching = false;
    });
  }
}
